import { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import FinancialEntryForm from '../components/dashboard/secretary/FinancialEntryForm';
import TransactionList from '../components/dashboard/secretary/TransactionList';
import WithdrawalRequest from '../components/dashboard/secretary/WithdrawalRequest';
import { useAuth } from '../hooks/useAuth';
import { useTransactionsCollection, useInstitutionsCollection } from '../hooks/useFirestore';
import { calculateInstitutionStats } from '../services/transactionService';
import { formatCurrency } from '../lib/utils/helpers';
import { LogOut } from 'lucide-react'; // Import de l'icône de déconnexion

interface Institution {
  id: string;
  institutionId: string;
  name: string;
  currency?: {
    code: string;
    symbol: string;
    name: string;
  };
}

const SecretaryDashboard: React.FC = () => {
  const { user, logout } = useAuth(); // Ajout de logout depuis useAuth
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);

  // Utiliser le hook pour récupérer les transactions
  const { 
    data: transactionsData, 
    loading: transactionsLoading, 
    getDocuments: getTransactions 
  } = useTransactionsCollection();

  // Récupérer les institutions pour avoir la devise
  const {
    data: institutionsData,
    loading: institutionsLoading,
    getDocuments: getInstitutions
  } = useInstitutionsCollection();

  const [stats, setStats] = useState({
    totalBalance: 0,
    monthlyIncome: 0,
    monthlyExpenses: 0,
    pendingWithdrawalRequests: 0,
  });

  const [institutionCurrency, setInstitutionCurrency] = useState({
    code: 'XOF',
    symbol: 'CFA',
    name: 'Franc CFA'
  });

  const [institutionName, setInstitutionName] = useState<string>('');

  // Charger les transactions et les institutions
  useEffect(() => {
    const loadData = async () => {
      if (user?.institutionId) {
        try {
          // Charger les transactions de l'institution
          await getTransactions({
            field: 'institutionId',
            operator: '==',
            value: user.institutionId,
            orderByField: 'createdAt',
            orderDir: 'desc'
          });

          // Charger les institutions pour récupérer la devise
          await getInstitutions();
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
        }
      }
    };

    loadData();
  }, [user?.institutionId, getTransactions, getInstitutions]);

  // Récupérer la devise de l'institution
  useEffect(() => {
    if (user?.institutionId && institutionsData && institutionsData.length > 0) {
      const institutions: Institution[] = (institutionsData as any) || [];
      
      // Trouver l'institution de l'utilisateur par institutionId
      const userInstitution = institutions.find(inst => 
        inst.institutionId === user.institutionId
      );
      
      if (userInstitution) {
        if (userInstitution.currency) {
          setInstitutionCurrency({
            code: userInstitution.currency.code || 'XOF',
            symbol: userInstitution.currency.symbol || 'CFA',
            name: userInstitution.currency.name || 'Franc CFA'
          });
        } else {
          setInstitutionCurrency({
            code: 'XOF',
            symbol: 'CFA',
            name: 'Franc CFA'
          });
        }
        
        setInstitutionName(userInstitution.name || '');
      } else {
        // Institution non trouvée
        setInstitutionCurrency({
          code: 'XOF',
          symbol: 'CFA',
          name: 'Franc CFA'
        });
        setInstitutionName('');
      }
    }
  }, [user?.institutionId, institutionsData]);

  // Calculer les statistiques quand les données changent
  useEffect(() => {
    if (transactionsData && transactionsData.length > 0 && user?.institutionId) {
      updateStatistics();
    }
  }, [transactionsData, user?.institutionId]);

  // Mettre à jour les statistiques
  const updateStatistics = async () => {
    try {
      const institutionStats = await calculateInstitutionStats(user?.institutionId || '');
      
      // Compter les demandes de retrait en attente
      const pendingWithdrawals = (transactionsData || []).filter(
        (transaction: any) => 
          transaction.category === 'withdrawal_request' && 
          transaction.status === 'pending'
      ).length;

      setStats({
        totalBalance: institutionStats.balance || 0,
        monthlyIncome: institutionStats.currentMonthIncome || 0,
        monthlyExpenses: institutionStats.currentMonthExpenses || 0,
        pendingWithdrawalRequests: pendingWithdrawals,
      });
    } catch (error) {
      console.error('Erreur lors du calcul des statistiques:', error);
    }
  };

  // Extraire le prénom du displayName
  const getFirstName = () => {
    if (!user?.displayName) return 'Utilisateur';
    
    const parts = user.displayName.split(' ');
    return parts[0] || 'Utilisateur';
  };

  // Gérer le succès d'une action
  const handleSuccess = () => {
    // Recharger les transactions
    if (user?.institutionId) {
      getTransactions({
        field: 'institutionId',
        operator: '==',
        value: user.institutionId,
        orderByField: 'createdAt',
        orderDir: 'desc'
      });
    }
  };

  // Écouter les événements de mise à jour
  useEffect(() => {
    const handleDataUpdated = () => {
      handleSuccess();
    };

    window.addEventListener('data-updated', handleDataUpdated);
    
    return () => {
      window.removeEventListener('data-updated', handleDataUpdated);
    };
  }, [user?.institutionId, getTransactions]);

  // Fonction de déconnexion
  const handleLogout = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      await logout();
      window.location.href = '/login';
    }
  };

  const isLoading = transactionsLoading || institutionsLoading;

  // Afficher un indicateur de chargement
  if (isLoading && (!transactionsData || !institutionsData)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header avec bouton de déconnexion */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Bonjour, {getFirstName()} 👋
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Gestion financière de {institutionName || user?.institutionId || 'votre institution'}
          </p>
          <div className="mt-1 text-xs text-gray-500">
            Devise: {institutionCurrency.name} ({institutionCurrency.code})
          </div>
        </div>

        {/* Bouton de déconnexion */}
        <Button
          variant="outline"
          onClick={handleLogout}
          className="flex items-center px-4 py-2 text-red-600 hover:bg-red-50 border-red-200"
        >
          <LogOut className="h-5 w-5 mr-2" />
          Déconnexion
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <span className="text-blue-600 text-xl">💰</span>
                </div>
              </div>
              <div className="ml-5">
                <p className="text-sm font-medium text-gray-500 truncate">
                  Solde total
                </p>
                <p className="mt-1 text-3xl font-semibold text-gray-900">
                  {formatCurrency(stats.totalBalance, institutionCurrency.code)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {institutionCurrency.symbol}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 bg-green-100 rounded-lg">
                  <span className="text-green-600 text-xl">📈</span>
                </div>
              </div>
              <div className="ml-5">
                <p className="text-sm font-medium text-gray-500 truncate">
                  Revenus du mois
                </p>
                <p className="mt-1 text-3xl font-semibold text-gray-900">
                  {formatCurrency(stats.monthlyIncome, institutionCurrency.code)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {institutionCurrency.symbol}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 bg-red-100 rounded-lg">
                  <span className="text-red-600 text-xl">📉</span>
                </div>
              </div>
              <div className="ml-5">
                <p className="text-sm font-medium text-gray-500 truncate">
                  Dépenses du mois
                </p>
                <p className="mt-1 text-3xl font-semibold text-gray-900">
                  {formatCurrency(stats.monthlyExpenses, institutionCurrency.code)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {institutionCurrency.symbol}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <span className="text-yellow-600 text-xl">⏳</span>
                </div>
              </div>
              <div className="ml-5">
                <p className="text-sm font-medium text-gray-500 truncate">
                  Demandes de retrait en attente
                </p>
                <p className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats.pendingWithdrawalRequests}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Transactions en attente
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Information sur la devise */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="p-2 bg-blue-100 rounded-lg">
              <span className="text-blue-600">💱</span>
            </div>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-blue-900">
              Devise de l'institution: <span className="font-bold">{institutionCurrency.name}</span>
            </p>
            <p className="text-xs text-blue-700">
              Symbole: <span className="font-mono font-bold">{institutionCurrency.symbol}</span> | 
              Code: <span className="font-mono">{institutionCurrency.code}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <div className="flex space-x-4">
          <Button
            variant="primary"
            onClick={() => setShowEntryForm(true)}
            className="flex items-center"
          >
            <span className="mr-2">+</span> Nouvelle entrée financière
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowWithdrawalForm(true)}
            className="flex items-center"
          >
            <span className="mr-2">🏧</span> Demande de retrait
          </Button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Transactions récentes
        </h2>
        <TransactionList 
          institutionId={user?.institutionId || ''}
        />
      </div>

      {/* Modals */}
      <Modal
        isOpen={showEntryForm}
        onClose={() => setShowEntryForm(false)}
        title="Nouvelle entrée financière"
      >
        <FinancialEntryForm 
          onSuccess={() => {
            setShowEntryForm(false);
            handleSuccess();
          }} 
          onCancel={() => setShowEntryForm(false)}
        />
      </Modal>

      <Modal
        isOpen={showWithdrawalForm}
        onClose={() => setShowWithdrawalForm(false)}
        title="Demande de retrait"
      >
        <WithdrawalRequest 
          onSuccess={() => {
            setShowWithdrawalForm(false);
            handleSuccess();
          }} 
          onCancel={() => setShowWithdrawalForm(false)}
        />
      </Modal>
    </div>
  );
};

export default SecretaryDashboard;